import { useEffect, useState } from 'react';
import { DEFAULT_LOCATION } from '../constants';
import type {
  DashboardData,
  MapTopic,
  NewsMode,
  Segment,
  TopicRecord,
  WordCloudEntry
} from '../types';
import {
  fetchLocations,
  fetchMapTopics,
  fetchVipData,
  fetchTopLocations,
  fetchTopicTension,
  fetchDiasporaTopics,
  fetchSentiment as fetchObjectivity,
  fetchCompass,
  fetchWordCloud
} from '../api/bqQueries';
import coordinatesData from '../data/coordinates';

const mapTopicsToRecords = (topics: MapTopic[]): TopicRecord[] =>
  topics.map((entry) => ({
    location: entry.location,
    topics: entry.topics,
    engagement: entry.engagement
  }));

const mergeTopics = (primary: MapTopic[], extras: MapTopic[]): MapTopic[] => {
  const map: Record<string, MapTopic> = {};
  [...primary, ...extras].forEach((item) => {
    if (!map[item.location]) {
      map[item.location] = { ...item, topics: [...item.topics] };
    } else {
      const existing = map[item.location];
      const mergedTopics = [...existing.topics];
      item.topics.forEach((t) => {
        if (!mergedTopics.includes(t)) mergedTopics.push(t);
      });
      map[item.location] = {
        location: item.location,
        topics: mergedTopics.slice(0, 3),
        engagement: (existing.engagement ?? 0) + (item.engagement ?? 0)
      };
    }
  });
  return Object.values(map);
};

const buildWordCloudRecord = (values: WordCloudEntry[][]): Record<Segment, WordCloudEntry[]> => ({
  Left: values[0] ?? [],
  Neutral: values[1] ?? [],
  Right: values[2] ?? []
});

export function useDashboardData(week: string, locationForCompass?: string) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!week) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        let [
          locationsList,
          topLocationsPolitical,
          topicTensionPolitical,
          topicTensionGeneral,
          vipDataPolitical,
          vipDataGeneral,
          objectivityPolitical,
          objectivityGeneral
        ] =
          await Promise.all([
            fetchLocations(week),
            fetchTopLocations('Political', week),
            fetchTopicTension('Political', week),
            fetchTopicTension('General', week),
            fetchVipData('Political', week),
            fetchVipData('General', week),
            fetchObjectivity('Political', week),
            fetchObjectivity('General', week)
          ]);

        if (objectivityPolitical.length === 0) {
          objectivityPolitical = await fetchObjectivity('Political', week);
        }
        if (objectivityGeneral.length === 0) {
          objectivityGeneral = await fetchObjectivity('General', week);
        }
        const compass = await fetchCompass(week, locationForCompass ?? DEFAULT_LOCATION);

        const [
          mapTopicsPoliticalLeft,
          mapTopicsPoliticalNeutral,
          mapTopicsPoliticalRight,
          diasporaPoliticalLeft,
          diasporaPoliticalNeutral,
          diasporaPoliticalRight
        ] = await Promise.all([
          fetchMapTopics('Political', week, 'Left'),
          fetchMapTopics('Political', week, 'Neutral'),
          fetchMapTopics('Political', week, 'Right'),
          fetchDiasporaTopics('Political', week, 'Left'),
          fetchDiasporaTopics('Political', week, 'Neutral'),
          fetchDiasporaTopics('Political', week, 'Right')
        ]);
        const [
          mapTopicsGeneralLeft,
          mapTopicsGeneralNeutral,
          mapTopicsGeneralRight,
          diasporaGeneralLeft,
          diasporaGeneralNeutral,
          diasporaGeneralRight
        ] = await Promise.all([
          fetchMapTopics('General', week, 'Left'),
          fetchMapTopics('General', week, 'Neutral'),
          fetchMapTopics('General', week, 'Right'),
          fetchDiasporaTopics('General', week, 'Left'),
          fetchDiasporaTopics('General', week, 'Neutral'),
          fetchDiasporaTopics('General', week, 'Right')
        ]);

        const segments: Segment[] = ['Left', 'Neutral', 'Right'];
        const [wordCloudPolitical, wordCloudGeneral] = await Promise.all([
          Promise.all(
            segments.map((segment) =>
              fetchWordCloud('Political', week, locationForCompass ?? DEFAULT_LOCATION, segment)
            )
          ),
          Promise.all(
            segments.map((segment) =>
              fetchWordCloud('General', week, locationForCompass ?? DEFAULT_LOCATION, segment)
            )
          )
        ]);

        if (cancelled) {
          return;
        }

        const wordCloudData = {
          political: buildWordCloudRecord(wordCloudPolitical),
          general: buildWordCloudRecord(wordCloudGeneral)
        };

        const prepared: DashboardData = {
          locationCounts: {
            Left: topLocationsPolitical.singles.Left,
            Neutral: topLocationsPolitical.singles.Neutral,
            Right: topLocationsPolitical.singles.Right
          },
          locationStacks: topLocationsPolitical.stacks,
          topics: {
            political: {
              Left: mapTopicsToRecords(mergeTopics(mapTopicsPoliticalLeft, diasporaPoliticalLeft)),
              Neutral: mapTopicsToRecords(mergeTopics(mapTopicsPoliticalNeutral, diasporaPoliticalNeutral)),
              Right: mapTopicsToRecords(mergeTopics(mapTopicsPoliticalRight, diasporaPoliticalRight))
            },
            general: {
              Left: mapTopicsToRecords(mergeTopics(mapTopicsGeneralLeft, diasporaGeneralLeft)),
              Neutral: mapTopicsToRecords(mergeTopics(mapTopicsGeneralNeutral, diasporaGeneralNeutral)),
              Right: mapTopicsToRecords(mergeTopics(mapTopicsGeneralRight, diasporaGeneralRight))
            }
          },
          coordinates: coordinatesData,
          topicTension: { political: topicTensionPolitical, general: topicTensionGeneral },
          vipHype: {
            political: vipDataPolitical.hype,
            general: vipDataGeneral.hype
          },
          vipTopics: {
            political: vipDataPolitical.topics,
            general: vipDataGeneral.topics
          },
          objectivity: { political: objectivityPolitical, general: objectivityGeneral },
          compass,
          wordCloud: wordCloudData,
          locations: locationsList
        };

        setData(prepared);
        setInitialized(true);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load data';
        setError(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [week]);

  useEffect(() => {
    if (!initialized) return;
    let cancelled = false;
    fetchCompass(week, locationForCompass ?? DEFAULT_LOCATION)
      .then((compassData) => {
        if (cancelled) return;
        setData((prev) => (prev ? { ...prev, compass: compassData } : prev));
      })
      .catch(() => {
        // ignore compass errors
      });
    return () => {
      cancelled = true;
    };
  }, [week, locationForCompass, initialized]);

  useEffect(() => {
    if (!initialized) return;
    let cancelled = false;
    const segments: Segment[] = ['Left', 'Neutral', 'Right'];
    async function loadWordCloud() {
      try {
        const [political, general] = await Promise.all([
          Promise.all(
            segments.map((segment) =>
              fetchWordCloud('Political', week, locationForCompass ?? DEFAULT_LOCATION, segment)
            )
          ),
          Promise.all(
            segments.map((segment) =>
              fetchWordCloud('General', week, locationForCompass ?? DEFAULT_LOCATION, segment)
            )
          )
        ]);
        if (cancelled) return;
        setData((prev) =>
          prev
            ? {
                ...prev,
                wordCloud: {
                  political: buildWordCloudRecord(political),
                  general: buildWordCloudRecord(general)
                }
              }
            : prev
        );
      } catch {
        // ignore
      }
    }
    loadWordCloud();
    return () => {
      cancelled = true;
    };
  }, [week, locationForCompass, initialized]);

  return { data, loading, error, initialized };
}
